import React from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react'
import { action } from 'mobx'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from 'components/ModalSheet'
import { Form, FormGroupContainer, TextInputContainer } from 'components/inputs'
import Button from 'components/Button'
import Checkbox from 'components/Checkbox'
import Radio from 'components/Radio'
import { CountryInput } from 'components/inputs/CountryInput'
import {
  CustomSelect,
  ItemSource,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import { TabBar, TabItem } from 'components/TabBar'
import {
  areSelectOptionsEqual,
  SelectOption,
} from 'jobs/questions/Select/SelectQuestionViewModel'
import { US_STATES_AND_TERRITORIES } from 'jobs/info-gathering/editors/us-states-territories'
import EditRefundDialogViewModel, {
  REFUND_TAB_LABELS,
} from './EditRefundDialogViewModel'
import TaxRefundMethodEditorViewModel, {
  DeliveryMethodKind,
} from 'jobs/info-gathering/tax-refund-method/editors/TaxRefundMethodEditorViewModel'
import BankAccountEditorViewModel from 'jobs/info-gathering/tax-refund-method/editors/BankAccountEditorViewModel'
import CheckDeliveryMethodEditorViewModel from 'jobs/info-gathering/tax-refund-method/editors/CheckDeliveryMethodEditorViewModel'
import PhysicalAddressEditorViewModel from 'jobs/info-gathering/editors/PhysicalAddressEditorViewModel'
import {
  InfoGatheringDialogFieldGrid,
  InfoGatheringDialogScrollArea,
  InfoGatheringDialogSubsectionHeader,
} from 'jobs/info-gathering/components/InfoGatheringDialog'
import styles from './EditRefundDialog.sass'

const US_STATE_OPTIONS: SelectOption[] = US_STATES_AND_TERRITORIES.map(
  ([code, label]) => ({ id: code, text: label }),
)

const stateItemToString = (item: SelectOption) => item.text

const ACCOUNT_TYPE_OPTIONS: SelectOption[] = [
  { id: 'Checking', text: 'Checking' },
  { id: 'Savings', text: 'Savings' },
]

const accountTypeItemToString = (item: SelectOption) => item.text

const METHOD_LABELS: Record<DeliveryMethodKind, string> = {
  directDeposit: 'Direct deposit',
  check: 'Paper check',
  futureTaxCredit: 'Future tax credit',
}

interface Props {
  vm: EditRefundDialogViewModel
}

export default observer(function EditRefundDialog({ vm }: Props) {
  return (
    <ModalSheet
      showing={vm.showing}
      onRequestClose={vm.submit.pending ? undefined : vm.close}
      disableCloseOnOutsideClick
      disableCloseOnEsc
    >
      <ModalSheetHeader withCloseButton bordered={!vm.hasMultipleTabs}>
        <ModalSheetTitle emphasized>Fill in refund info</ModalSheetTitle>
      </ModalSheetHeader>
      {vm.hasMultipleTabs && (
        <TabBar bordered>
          {vm.availableTabs.map((tab) => (
            <TabItem
              key={tab}
              active={vm.activeTab === tab}
              onClick={() => vm.setTab(tab)}
              dataTest={`edit-refund-tab-${tab}`}
            >
              {REFUND_TAB_LABELS[tab]}
            </TabItem>
          ))}
        </TabBar>
      )}
      <ModalSheetContent>
        <ModalSheetSection spaced>
          <InfoGatheringDialogScrollArea>
            <Form>
              <div
                className={cx(
                  vm.activeTab !== 'personal' && styles.tabPaneHidden,
                )}
              >
                <RefundEditor editor={vm.personalRefundEditorVm} />
              </div>
              <div
                className={cx(
                  vm.activeTab !== 'business' && styles.tabPaneHidden,
                )}
              >
                <RefundEditor editor={vm.businessRefundEditorVm} />
              </div>
            </Form>
          </InfoGatheringDialogScrollArea>
        </ModalSheetSection>
      </ModalSheetContent>
      <ModalSheetFooter>
        {vm.hasMultipleTabs && !vm.isFirstTab && (
          <ModalSheetFooterButtonGroup>
            <Button
              type="secondary"
              onClick={vm.goToPreviousTab}
              disabled={vm.submit.pending}
              data-test="edit-refund-back-button"
            >
              Back
            </Button>
          </ModalSheetFooterButtonGroup>
        )}
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          {vm.hasMultipleTabs && !vm.isLastTab && (
            <Button
              type="secondary"
              onClick={vm.skipCurrentTab}
              disabled={vm.submit.pending}
              data-test="edit-refund-skip-button"
            >
              Skip {REFUND_TAB_LABELS[vm.activeTab]}
            </Button>
          )}
          <Button
            type="primary"
            onClick={vm.submit}
            loading={vm.submit.pending}
            disabled={vm.submit.pending}
            data-test="edit-refund-submit-button"
          >
            {vm.isLastTab ? 'Save & Close' : 'Save & Continue'}
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})

interface RefundEditorProps {
  editor: TaxRefundMethodEditorViewModel
}

const RefundEditor = observer(function RefundEditor({
  editor,
}: RefundEditorProps) {
  return (
    <>
      <InfoGatheringDialogSubsectionHeader>
        Choose the method
      </InfoGatheringDialogSubsectionHeader>
      <div className={styles.methodPicker}>
        {editor.availableMethods.map((method) => (
          <div
            key={method}
            className={cx(
              styles.methodCard,
              editor.selectedMethod === method && styles.methodCardSelected,
            )}
            onClick={action(() => editor.selectMethod(method))}
            data-test={`edit-refund-method-${method}`}
          >
            <Radio selected={editor.selectedMethod === method} />
            <span className={styles.methodCardLabel}>
              {METHOD_LABELS[method]}
            </span>
          </div>
        ))}
      </div>
      {editor.selectionError && (
        <div className={styles.methodSelectionError}>
          {editor.selectionError}
        </div>
      )}

      {editor.selectedMethod === 'directDeposit' && (
        <DirectDepositFields vm={editor.directDepositVm.bankAccountVm} />
      )}
      {editor.selectedMethod === 'check' && <CheckFields vm={editor.checkVm} />}
    </>
  )
})

interface DirectDepositFieldsProps {
  vm: BankAccountEditorViewModel
}

const DirectDepositFields = observer(function DirectDepositFields({
  vm,
}: DirectDepositFieldsProps) {
  const accountTypeItemSource = React.useMemo(
    () =>
      ItemSource.static<SelectOption>(ACCOUNT_TYPE_OPTIONS, (item, text) =>
        item.text.toLowerCase().includes(text.toLowerCase()),
      ),
    [],
  )
  const accountTypeSelectProps = useCustomSelectStateFromItemSource({
    itemSource: accountTypeItemSource,
  })
  const selectedAccountType = ACCOUNT_TYPE_OPTIONS.find(
    (o) => o.id === vm.accountType,
  )
  return (
    <>
      <InfoGatheringDialogSubsectionHeader bordered>
        New bank account
      </InfoGatheringDialogSubsectionHeader>
      <InfoGatheringDialogFieldGrid>
        <FormGroupContainer
          label="Account type"
          htmlFor="accountType"
          error={vm.validation.getError('accountType')}
        >
          <CustomSelect<SelectOption, false>
            {...accountTypeSelectProps}
            multi={false}
            value={selectedAccountType}
            itemToString={accountTypeItemToString}
            areItemsEqual={areSelectOptionsEqual}
            placeholder="Select account type"
            onChange={action((selection: SelectOption | undefined) => {
              vm.accountType =
                (selection?.id as 'Checking' | 'Savings' | undefined) ?? ''
            })}
            data-test="edit-refund-account-type"
          />
        </FormGroupContainer>
        <TextInputContainer
          model={vm}
          field="bankName"
          name="bankName"
          label="Bank name"
          sublabel="(optional)"
          error={vm.validation.getError('bankName')}
          data-test="edit-refund-bank-name"
        />
        <TextInputContainer
          model={vm}
          field="routingNumber"
          name="routingNumber"
          label="Routing number"
          error={vm.validation.getError('routingNumber')}
          data-test="edit-refund-routing-number"
        />
        <TextInputContainer
          model={vm}
          field="accountNumber"
          name="accountNumber"
          label="Account number"
          error={vm.validation.getError('accountNumber')}
          data-test="edit-refund-account-number"
        />
      </InfoGatheringDialogFieldGrid>
    </>
  )
})

interface CheckFieldsProps {
  vm: CheckDeliveryMethodEditorViewModel
}

const CheckFields = observer(function CheckFields({ vm }: CheckFieldsProps) {
  return (
    <>
      <div
        className={styles.checkboxRow}
        onClick={action(() => {
          vm.useSameAddressAsContact = !vm.useSameAddressAsContact
        })}
      >
        <Checkbox checked={vm.useSameAddressAsContact} muteClickEvent />
        <span>Use same address as contact information</span>
      </div>
      {!vm.useSameAddressAsContact && (
        <CheckAddressFields vm={vm.physicalAddressVm} />
      )}
    </>
  )
})

interface CheckAddressFieldsProps {
  vm: PhysicalAddressEditorViewModel
}

const CheckAddressFields = observer(function CheckAddressFields({
  vm,
}: CheckAddressFieldsProps) {
  const isUS = vm.country === 'US'
  const stateItemSource = React.useMemo(
    () =>
      ItemSource.static<SelectOption>(US_STATE_OPTIONS, (item, text) =>
        item.text.toLowerCase().includes(text.toLowerCase()),
      ),
    [],
  )
  const stateSelectProps = useCustomSelectStateFromItemSource({
    itemSource: stateItemSource,
  })
  const selectedState = US_STATE_OPTIONS.find((o) => o.id === vm.region)

  return (
    <InfoGatheringDialogFieldGrid>
      <TextInputContainer
        model={vm}
        field="addressLine1"
        name="addressLine1"
        label="Address line 1"
        error={vm.validation.getError('addressLine1')}
        data-test="edit-refund-address-line1"
      />
      <TextInputContainer
        model={vm}
        field="addressLine2"
        name="addressLine2"
        label="Address line 2"
        sublabel="(optional)"
        error={vm.validation.getError('addressLine2')}
        data-test="edit-refund-address-line2"
      />
      <TextInputContainer
        model={vm}
        field="city"
        name="city"
        label="City"
        error={vm.validation.getError('city')}
        data-test="edit-refund-city"
      />
      <TextInputContainer
        model={vm}
        field="postalCode"
        name="postalCode"
        label="ZIP / Postal code"
        error={vm.validation.getError('postalCode')}
        data-test="edit-refund-postal-code"
      />
      {isUS ? (
        <FormGroupContainer
          label="State or territory"
          htmlFor="region"
          error={vm.validation.getError('region')}
        >
          <CustomSelect<SelectOption, false>
            {...stateSelectProps}
            multi={false}
            value={selectedState}
            itemToString={stateItemToString}
            areItemsEqual={areSelectOptionsEqual}
            placeholder="Select state"
            onChange={action((selection: SelectOption | undefined) => {
              vm.region = selection?.id ?? ''
            })}
            data-test="edit-refund-region"
          />
        </FormGroupContainer>
      ) : (
        <TextInputContainer
          model={vm}
          field="region"
          name="region"
          label="Region"
          error={vm.validation.getError('region')}
          data-test="edit-refund-region"
        />
      )}
      <FormGroupContainer
        label="Country"
        htmlFor="country"
        error={vm.validation.getError('country')}
      >
        <CountryInput
          value={vm.country}
          onChange={action((code: string) => {
            vm.country = code
          })}
          data-test="edit-refund-country"
        />
      </FormGroupContainer>
    </InfoGatheringDialogFieldGrid>
  )
})
